import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { getApiV1Url } from '../config/api';
import Header from '../components/Header/Header';
import LoadingSpinner from '../components/LoadingSpinner';
import ColumnFilter from '../components/Filters/ColumnFilter';
import { useColumnDrag } from '../hooks/useColumnDrag';
import Breadcrumbs from '../components/Breadcrumbs';
import * as XLSX from 'xlsx';
import './RosakkreditatsiyaPage.css';

interface TestingLaboratory {
  id: number;
  actualization_date: string | null;
  federal_district: string | null;
  full_name: string | null;
  accrediting_authority: string | null;
  expert_organization: string | null;
  registration_number: string | null;
  registration_date: string | null;
  expiration_date: string | null;
  suspension_date: string | null;
  status: string | null;
  legal_address: string | null;
  actual_address: string | null;
  director_info: string | null;
  contact_person_info: string | null;
  competence_confirmation_date: string | null;
  measurement_methods: string | null;
  determined_indicators: string | null;
  tn_ved_codes: string | null;
  okp_codes: string | null;
  accreditation_scope_description: string | null;
  website_link: string | null;
  rosaccreditation_link: string | null;
  tests_and_terms: string | null;
  comments: string | null;
  created_at: string;
  updated_at: string;
}

interface ColumnConfig {
  key: keyof TestingLaboratory;
  label: string;
  render?: (value: any, item: TestingLaboratory) => React.ReactNode;
}

const RosakkreditatsiyaILPage: React.FC = () => {
  const [items, setItems] = useState<TestingLaboratory[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState<keyof TestingLaboratory | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => {
    const saved = localStorage.getItem('testing-laboratory-visible-columns');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          return parsed;
        }
      } catch {
        // ignore
      }
    }
    return [
      'full_name',
      'registration_number',
      'status',
      'federal_district',
      'registration_date',
      'expiration_date',
      'suspension_date',
      'legal_address',
      'rosaccreditation_link',
      'actual_address',
      'director_info',
      'contact_person_info',
      'competence_confirmation_date',
      'measurement_methods',
      'determined_indicators',
      'tn_ved_codes',
      'okp_codes',
      'accreditation_scope_description',
      'website_link',
      'tests_and_terms',
      'comments',
      'actualization_date',
    ];
  });

  const defaultColumnOrder: string[] = [
    'full_name',
    'registration_number',
    'status',
    'federal_district',
    'registration_date',
    'expiration_date',
    'suspension_date',
    'legal_address',
    'rosaccreditation_link',
    'actual_address',
    'director_info',
    'contact_person_info',
    'competence_confirmation_date',
    'measurement_methods',
    'determined_indicators',
    'tn_ved_codes',
    'okp_codes',
    'accreditation_scope_description',
    'website_link',
    'tests_and_terms',
    'comments',
    'actualization_date',
    'created_at',
    'updated_at',
  ];

  const [expandedCells, setExpandedCells] = useState<Record<string, boolean>>({});

  const {
    columnOrder,
    draggedColumn,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragEnd,
    resetColumnOrder,
  } = useColumnDrag({
    defaultColumnOrder,
    storageKey: 'testing-laboratory-column-order',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const access = localStorage.getItem('access');
        const response = await axios.get<TestingLaboratory[]>(getApiV1Url('TESTING_LABORATORY'), {
          headers: access ? { Authorization: `Bearer ${access}` } : {},
        });
        setItems(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Ошибка загрузки данных испытательных лабораторий:', error);
        setItems([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSort = useCallback((field: keyof TestingLaboratory) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  }, [sortField, sortDirection]);

  const sortedItems = useMemo(() => {
    if (!sortField) return items;

    return [...items].sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc'
          ? aValue.localeCompare(bValue, 'ru')
          : bValue.localeCompare(aValue, 'ru');
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });
  }, [items, sortField, sortDirection]);

  const getSortIcon = (field: keyof TestingLaboratory) => {
    if (sortField !== field) return '';
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString; 
  
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
  
      return `${day}.${month}.${year}`;
    } catch {
      return dateString;
    }
  };

  const columns: ColumnConfig[] = [
    { key: 'full_name', label: 'Наименование' },
    { key: 'registration_number', label: 'Регистрационный номер' },
    { key: 'status', label: 'Статус' },
    { key: 'federal_district', label: 'Федеральный округ' },
    { key: 'registration_date', label: 'Дата внесения в РАЛ', render: (value) => formatDate(value) },
    { key: 'expiration_date', label: 'Срок окончания', render: (value) => formatDate(value) },
    { key: 'suspension_date', label: 'Дата приостановления', render: (value) => formatDate(value) },
    { key: 'legal_address', label: 'Юридический адрес' },
    { key: 'rosaccreditation_link', label: 'Ссылка на Росаккредитацию', render: (value) => (
      value ? (
        <a href={value} target="_blank" rel="noopener noreferrer">
          Ссылка
        </a>
      ) : '-'
    ) },
    { key: 'actual_address', label: 'Фактический адрес' },
    { key: 'director_info', label: 'Руководитель' },
    { key: 'contact_person_info', label: 'Контактное лицо' },
    { key: 'competence_confirmation_date', label: 'Дата подтверждения компетентности' },
    { key: 'measurement_methods', label: 'МВИ' },
    { key: 'determined_indicators', label: 'Определяемые показатели' },
    { key: 'tn_ved_codes', label: 'Коды ТН ВЭД ТС' },
    { key: 'okp_codes', label: 'Коды ОКП' },
    { key: 'accreditation_scope_description', label: 'Описание области аккредитации' },
    { key: 'website_link', label: 'Сайт', render: (value) => (
      value ? (
        <a href={value} target="_blank" rel="noopener noreferrer">
          Сайт
        </a>
      ) : '-'
    ) },
    { key: 'tests_and_terms', label: 'Испытания и сроки' },
    { key: 'comments', label: 'Комментарии' },
    { key: 'actualization_date', label: 'Дата актуализации' },
    { key: 'created_at', label: 'Создано' },
    { key: 'updated_at', label: 'Обновлено' },
  ];

  const handleColumnVisibilityChange = (newVisible: string[]) => {
    setVisibleColumns(newVisible);
    localStorage.setItem('testing-laboratory-visible-columns', JSON.stringify(newVisible));
  };

  const resetColumnSettings = () => {
    setVisibleColumns(defaultColumnOrder);
    resetColumnOrder();
    localStorage.setItem('testing-laboratory-visible-columns', JSON.stringify(defaultColumnOrder));
  };

  const filteredColumnOrder = columnOrder.filter((key) => visibleColumns.includes(key));

  const getColumnByKey = (key: string) => columns.find((col) => col.key === key);

  const renderCellContent = (item: TestingLaboratory, column: ColumnConfig) => {
    const value = item[column.key];

    // Для ссылок и специальных рендеров column.render без обрезки
    if (column.key === 'website_link' || column.key === 'rosaccreditation_link') {
      return column.render ? column.render(value, item) : (value ?? '-');
    }

    if (column.key === 'registration_date' || column.key === 'expiration_date' || column.key === 'suspension_date') {
      const formatted = column.render ? column.render(value as string, item) : formatDate(value as string);
      return formatted ?? '-';
    }

    const rawText = (value ?? '') as string;
    const cellId = `${item.id}-${column.key}`;
    const isExpanded = expandedCells[cellId];

    if (rawText.length <= 100) {
      return rawText || '-';
    }

    const displayText = isExpanded ? rawText : `${rawText.slice(0, 100)}...`;

    return (
      <span>
        {displayText}{' '}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setExpandedCells((prev) => ({
              ...prev,
              [cellId]: !isExpanded,
            }));
          }}
          style={{
            border: 'none',
            background: 'none',
            padding: 0,
            margin: 0,
            cursor: 'pointer',
            color: '#007bff',
            fontSize: '12px',
          }}
        >
          {isExpanded ? 'Скрыть' : 'Подробнее'}
        </button>
      </span>
    );
  };

  const handleExportToExcel = () => {
    if (!items || items.length === 0) return;

    const exportData = items.map((item) => ({
      'Наименование': item.full_name ?? '',
      'Регистрационный номер': item.registration_number ?? '',
      'Статус': item.status ?? '',
      'Федеральный округ': item.federal_district ?? '',
      'Дата внесения в РАЛ': formatDate(item.registration_date),
      'Срок окончания': formatDate(item.expiration_date),
      'Дата приостановления': formatDate(item.suspension_date ?? ''),
      'Юридический адрес': item.legal_address ?? '',
      'Фактический адрес': item.actual_address ?? '',
      'Руководитель': item.director_info ?? '',
      'Контактное лицо': item.contact_person_info ?? '',
      'Дата подтверждения компетентности': item.competence_confirmation_date ?? '',
      'МВИ': item.measurement_methods ?? '',
      'Определяемые показатели': item.determined_indicators ?? '',
      'Коды ТН ВЭД ТС': item.tn_ved_codes ?? '',
      'Коды ОКП': item.okp_codes ?? '',
      'Описание области аккредитации': item.accreditation_scope_description ?? '',
      'Сайт': item.website_link ?? '',
      'Ссылка на Росаккредитацию': item.rosaccreditation_link ?? '',
      'Испытания и сроки': item.tests_and_terms ?? '',
      'Комментарии': item.comments ?? '',
      'Дата актуализации': item.actualization_date ?? '',
      'Создано': item.created_at ?? '',
      'Обновлено': item.updated_at ?? '',
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Испытательные лаборатории');
  XLSX.writeFile(workbook, 'ispytatelnye_laboratorii.xlsx');
  };

  if (loading) {
    return (
      <>
        <Header />
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <LoadingSpinner />
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="navigation_certificates">
        <Breadcrumbs
          items={[
            { label: 'Главная страница', to: '/' },
            { label: 'Испытательные лаборатории' },
          ]}
        />
        <button
          type="button"
          className="download-button"
          onClick={handleExportToExcel}
        >
          Выгрузить в Excel
        </button>
      </div>
      <div className="rosakkreditatsiya-page">
        <div className="rosakkreditatsiya-header">
          <h1>Испытательные лаборатории</h1>
          <p className="rosakkreditatsiya-subtitle">Всего записей: {items.length}</p>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 12 }}>
          <ColumnFilter
            columns={columns.map((c) => ({ key: c.key as string, label: c.label }))}
            visibleColumns={visibleColumns}
            onColumnVisibilityChange={handleColumnVisibilityChange}
            defaultColumns={defaultColumnOrder}
            onReset={resetColumnSettings}
          />
        </div>

        <div className="rosakkreditatsiya-table-container certificates-table">
          <table className="rosakkreditatsiya-table">
            <thead>
              <tr>
                {filteredColumnOrder.map((columnKey) => {
                  const column = getColumnByKey(columnKey);
                  if (!column) return null;
                  return (
                    <th
                      key={columnKey}
                      className="sortable"
                      onClick={() => handleSort(column.key)}
                      draggable
                      onDragStart={(e) => handleDragStart(e, columnKey)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, columnKey)}
                      onDragEnd={handleDragEnd}
                      style={{
                        cursor: 'grab',
                        backgroundColor: draggedColumn === columnKey ? '#005a94' : undefined,
                        opacity: draggedColumn && draggedColumn !== columnKey ? 0.7 : 1,
                      }}
                    >
                      {column.label} {getSortIcon(column.key)}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {sortedItems.length === 0 ? (
                <tr>
                  <td colSpan={filteredColumnOrder.length || 1} style={{ textAlign: 'center', padding: '20px' }}>
                    Нет данных для отображения
                  </td>
                </tr>
              ) : (
                sortedItems.map((item) => (
                  <tr key={item.id}>
                    {filteredColumnOrder.map((columnKey) => {
                      const column = getColumnByKey(columnKey);
                      if (!column) return null;
                      return (
                        <td key={columnKey}>
                          {renderCellContent(item, column)}
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default RosakkreditatsiyaILPage;

